import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  CURRENCY_COUNTRIES,
  FALLBACK_RATES_FROM_KES,
  getCountryByIso2,
  readStoredCurrencyIso2,
  writeStoredCurrencyIso2,
  type CurrencyCountry,
} from '@/lib/currencyCountries';

interface CurrencyContextValue {
  selectedCountry: CurrencyCountry;
  setSelectedCountryIso2: (iso2: string) => void;
  ratesLoading: boolean;
  /** Format a KES amount for the selected country (converts when not KES). */
  formatKesPrice: (amountKes: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

type RatesMap = Record<string, number>;

function mergeRates(fetched: RatesMap | null): RatesMap {
  const out: RatesMap = { ...FALLBACK_RATES_FROM_KES };
  if (fetched) {
    for (const [code, val] of Object.entries(fetched)) {
      if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
        out[code] = val;
      }
    }
  }
  return out;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [iso2, setIso2] = useState<string>(() => readStoredCurrencyIso2() ?? 'KE');
  const [rates, setRates] = useState<RatesMap | null>(null);
  const [ratesLoading, setRatesLoading] = useState(true);

  const selectedCountry = useMemo(() => getCountryByIso2(iso2) ?? CURRENCY_COUNTRIES[0], [iso2]);

  useEffect(() => {
    let cancelled = false;
    setRatesLoading(true);
    fetch('https://open.er-api.com/v6/latest/KES')
      .then((r) => r.json())
      .then((data: { result?: string; rates?: Record<string, number> }) => {
        if (cancelled) return;
        if (data?.result === 'success' && data.rates && typeof data.rates === 'object') {
          setRates(data.rates);
        } else {
          setRates(null);
        }
      })
      .catch(() => {
        if (!cancelled) setRates(null);
      })
      .finally(() => {
        if (!cancelled) setRatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setSelectedCountryIso2 = useCallback((next: string) => {
    const c = getCountryByIso2(next);
    if (!c) return;
    setIso2(c.iso2);
    writeStoredCurrencyIso2(c.iso2);
  }, []);

  const effectiveRates = useMemo(() => mergeRates(rates), [rates]);

  const formatKesPrice = useCallback(
    (amountKes: number): string => {
      if (!Number.isFinite(amountKes)) return '—';
      const cur = selectedCountry.currency;
      if (cur === 'KES') {
        return 'KES ' + amountKes.toLocaleString('en-KE', { maximumFractionDigits: 0 });
      }
      const perKes = effectiveRates[cur];
      if (perKes == null || !Number.isFinite(perKes)) {
        return 'KES ' + amountKes.toLocaleString('en-KE', { maximumFractionDigits: 0 });
      }
      const converted = amountKes * perKes;
      const fraction = ['JPY', 'KRW', 'UGX', 'TZS', 'RWF', 'VND'].includes(cur) ? 0 : 2;
      try {
        return new Intl.NumberFormat(selectedCountry.locale, {
          style: 'currency',
          currency: cur,
          minimumFractionDigits: fraction,
          maximumFractionDigits: fraction,
        }).format(converted);
      } catch {
        return `${cur} ${converted.toLocaleString(selectedCountry.locale, { maximumFractionDigits: fraction })}`;
      }
    },
    [effectiveRates, selectedCountry]
  );

  const value = useMemo(
    () => ({
      selectedCountry,
      setSelectedCountryIso2,
      ratesLoading,
      formatKesPrice,
    }),
    [selectedCountry, setSelectedCountryIso2, ratesLoading, formatKesPrice]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
